import axios from 'axios';
import moment from 'moment';
import XLSX from "xlsx-js-style";
import { enqueueSnackbar } from "notistack";
import { defaultOperators } from "react-querybuilder";
import { TEXT_OP_LABELS ,SELECT_OP_LABELS} from '../constants';
import { globalGetService, globalPostService } from "./globalApiServices";


export const createQueryParams = (paramsList) => {
  const searchParams = new URLSearchParams();

  Object.entries(paramsList).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.append(
        key,
        typeof value === "object" ? JSON.stringify(value) : value
      );
    }
  });

  return searchParams.toString();
};


export const uploadAttachments = async (files, module, leaseSlug, baseUrl) => {
  if (!leaseSlug || !files?.length) return;

  const formData = new FormData();
  files.forEach(file => {
    formData.append("files", file);
  });

  // Conditionally build the URL based on module value
  const modulePath = module === "lease" ? "" : `${module}/`;
  const url = `${baseUrl}/lease/${modulePath}${leaseSlug}/attachments`;

  try {
    const response = await globalPostService(url, formData);
    return response.data;
  } catch (error) {
    console.error("Upload error:", error?.response?.data || error.message);
    throw error;
  }
};


export const downloadAttachment = async (file_url, fileName) => {
  axios.get(file_url, { responseType: 'arraybuffer', })
      .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click()
      })
      .catch(error => { console.error("Download failed:", error); })
}

export const getFileName = (url) => {
  // Split the URL by '/' and get the last part
  const parts = url.split('/');
  const fileWithQuery = parts.pop(); // Get the last part with query params
  const fileName = fileWithQuery.split('?')[0]; // Remove query params
  return fileName;
}


export const downloadFile = async (
  fileUrl,
  fileName = null,
  setLoader = () => { }
) => {
  setLoader(true);

  try {
    const response = await globalGetService(fileUrl, {
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    if (!fileName) {
      fileName = fileUrl.split("/").pop();
    }
    const contentType = response.headers["content-type"];

    if (!fileName.includes(".")) {
      if (contentType.includes("pdf")) {
        fileName += ".pdf";
      } else if (
        contentType.includes("excel") ||
        contentType.includes("spreadsheet")
      ) {
        fileName += ".xlsx";
      } else if (contentType.includes("csv")) {
        fileName += ".csv";
      } else if (contentType.includes("image")) {
        fileName += ".png";
      } else if (contentType.includes("text")) {
        fileName += ".txt";
      } else {
        fileName += ".bin";
      }
    }

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    setLoader(false);
  } catch (error) {
    enqueueSnackbar(
      error.response?.data?.message || error?.message || "Download failed",
      { variant: "error" }
    );
    setLoader(false);
  }
};


export const cleanPayload = (obj) => {
  return Object.fromEntries(
    Object.entries(obj).filter(([key, value]) => {
      if (key === "assigned_code") return true;
      if (typeof value === "string" && value.trim() === "") return false;
      return value !== null && value !== undefined;
    })
  );
};

export const isValidYearRange = (date,type) => {
  const year = type === "isOnlyYear" ? date : moment(date).year();
  return year >= 1900 && year <= 2100;
};

export const sortingArray=(arrayName,sortingKey)=>{
  return arrayName?.sort((a, b) => {
    if (a[sortingKey] < b[sortingKey]) return 1;
    if (a[sortingKey] > b[sortingKey]) return -1;
    return 0;
});
}


export const getDateRange = (type) => {
  switch (type) {
    case "today":
      return {
        start: moment().format("YYYY-MM-DD"),
        end: moment().format("YYYY-MM-DD"),
      };

    case "yesterday":
      return {
        start: moment().subtract(1, "day").format("YYYY-MM-DD"),
        end: moment().subtract(1, "day").format("YYYY-MM-DD"),
      };

    case "7days":
      return {
        start: moment().subtract(6, "days").format("YYYY-MM-DD"),
        end: moment().format("YYYY-MM-DD"),
      };

    case "1month":
      return {
        start: moment().subtract(1, "month").format("YYYY-MM-DD"),
        end: moment().format("YYYY-MM-DD"),
      };

    default:
      return { start: null, end: null };
  }
};

export const textOps = defaultOperators.filter(op => TEXT_OP_LABELS[op.name]).map(op => ({ ...op, label: TEXT_OP_LABELS[op.name] }));
export const selectOps = defaultOperators.filter(op => ["=", "!="].includes(op.name)).map(op => ({ ...op, label: SELECT_OP_LABELS[op.name] }));

export const convertGraphRes = (graphsList) => {
  const graphs = Object.entries(graphsList).map(([title, graphsData]) => ({
    title,
    graphsData,
  }));
  return graphs;
};

const toCsvCell = (value) => {
  const v = value == null ? "" : String(value);
  const escaped = v.replace(/"/g, '""');
  return `"${escaped}"`;
};

export const exportToCsv = ({ data = [], fileName = "Export", columns = null }) => {
  if (!data.length) return;

  let headers;
  let bodyRows;

  if (columns) {
    headers = Object.keys(columns);
    bodyRows = data.map((row) =>
      headers
        .map((key) => {
          const column = columns[key];
          const value = typeof column === "function" ? column(row) : row[column];
          return toCsvCell(value);
        })
        .join(","),
    );
  } else {
    headers = Object.keys(data[0] || {});
    bodyRows = data.map((row) =>
      headers.map((key) => toCsvCell(row[key])).join(","),
    );
  }

  const csvContent = [headers.map(toCsvCell).join(","), ...bodyRows].join("\n");
  const today = new Date();
  const formattedDate = `${String(today.getDate()).padStart(2, "0")}-${String(today.getMonth() + 1).padStart(2, "0")}-${today.getFullYear()}`;
  const finalFileName = `${fileName}_${formattedDate}.csv`;
  const blob = new Blob([`\uFEFF${csvContent}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", finalFileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const exportToExcel = ({ data = [], fileName = "Export", sheetName = "Sheet1", columns = null, dataCellStyle = null, headerCellStyle = null, }) => {
  if (!data.length) return;
  let formattedData = data;
  if (columns) {
    formattedData = data.map((row) => {
      const obj = {};
      Object.keys(columns).forEach((key) => {
        const column = columns[key];
        obj[key] = typeof column === "function" ? column(row) : row[column];
      });
      return obj;
    });
  }
  const worksheet = XLSX.utils.json_to_sheet(formattedData);
  const range = XLSX.utils.decode_range( worksheet["!ref"] );
  const border = { top: { style: "thin", color: { rgb: "A6A6A6" }, }, bottom: { style: "thin", color: { rgb: "A6A6A6" }, }, left: { style: "thin", color: { rgb: "A6A6A6" }, }, right: { style: "thin", color: { rgb: "A6A6A6" }, }, };
  for (let row = range.s.r; row <= range.e.r; row++) {
    for (let col = range.s.c; col <= range.e.c; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: col, });
      if (!worksheet[cellAddress]) continue;
      if (row === 0) {
        worksheet[cellAddress].s = { font: { bold: true, sz: 11, }, fill: { patternType: "solid", fgColor: { rgb: "B6B6B6", }, }, alignment: { horizontal: "left", vertical: "center", }, ...headerCellStyle, border, };
      } else {
        const cellStyle = typeof dataCellStyle === "function" ? dataCellStyle(col) : dataCellStyle;
        worksheet[cellAddress].s = { font: { sz: 11, }, alignment: { horizontal: "left", vertical: "center", }, ...cellStyle, border, };
      }
    }
  }
  worksheet["!cols"] = Object.keys( formattedData[0] || {} ).map((key) => ({ wch: Math.max( key.length, ...formattedData.map((row) => row[key] ? row[key].toString().length : 0 ) ) + 2, }));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet( workbook, worksheet, sheetName );
  const today = new Date();
  const formattedDate = `${String( today.getDate() ).padStart(2, "0")}-${String( today.getMonth() + 1 ).padStart(2, "0")}-${today.getFullYear()}`;
  const finalFileName = `${fileName}_${formattedDate}.xlsx`;
  XLSX.writeFile(workbook, finalFileName);
};


export const formatExcelDate = (value) => {
  if (!value) return null;
  const toLocalDateString = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };
  if (isNaN(value) && !Number(value)) {
    const trimmed = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const date = new Date(trimmed);
      return isNaN(date.getTime()) ? null : date.toISOString().split("T")[0];
    }
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmed)) {
      const [day, month, year] = trimmed.split("/").map(Number);
      const date = new Date(year, month - 1, day);
      return ( date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ) ? toLocalDateString(date) : null;
    }
    if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(trimmed)) {
      const [day, month, year] = trimmed.split("-").map(Number);
      const date = new Date(year, month - 1, day);
      return ( date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ) ? toLocalDateString(date) : null;
    }
    return null;
  }
  const excelDate = Number(value);
  if (isNaN(excelDate)) return null;
  const jsDate = new Date((excelDate - 25569) * 86400 * 1000);
  return isNaN(jsDate.getTime()) ? null : jsDate.toISOString().split("T")[0];
};

export const validateExcelDate = (value) => {
  if (!value) return true;
  if (value instanceof Date) { return !isNaN(value.getTime()); }
  if (typeof value === "number") { const jsDate = new Date((value - 25569) * 86400 * 1000); return !isNaN(jsDate.getTime()); }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return false;
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const date = new Date(trimmed);
      return !isNaN(date.getTime());
    }
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmed)) {
      const [day, month, year] = trimmed.split("/").map(Number);
      const date = new Date(year, month - 1, day);
      return (
        date.getFullYear() === year &&
        date.getMonth() === month - 1 &&
        date.getDate() === day
      );
    }
    if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(trimmed)) {
      const [day, month, year] = trimmed.split("-").map(Number);
      const date = new Date(year, month - 1, day);
      return (
        date.getFullYear() === year &&
        date.getMonth() === month - 1 &&
        date.getDate() === day
      );
    }
    return false;
  }
  return false;
};

// API sends "-" as a placeholder for "no value" on amount/value fields; it must never reach Number()/NaN.
export const isValidAmount = (value) =>
  value !== null &&
  value !== undefined &&
  value !== "" &&
  value !== "-" &&
  !Number.isNaN(Number(value));

export const formatCurrencyAmount = (amount) => {
  if (amount === "-") {
    return "--";
  }
  if (amount === null || amount === undefined || amount === "") {
    return "-";
  }
  if (!isValidAmount(amount)) {
    return "--";
  }
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(amount));
};